/**
 * Section 20 — Legal & Disclaimer.
 *
 * Risk disclaimer + ToS placeholders + privacy summary + SEBI framing
 * note. The risk-disclaimer is the load-bearing part; T&C and Privacy
 * full text need to be drafted with a lawyer before public launch.
 */
import React from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import GuideCallout from '../GuideCallout';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_legal: GuideSection = {
  id: 'legal',
  label: 'Legal & Disclaimer',
  group: 'help',
  icon: <GavelIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Legal & Disclaimer</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Read this before placing your first live trade. It's short, plain-English, and important.
      </Typography>

      <H id="risk-warning">Market Risk Warning</H>
      <GuideCallout kind="danger" title="Trading involves substantial risk of loss">
        <P>
          earngush executes real-money orders against live exchanges. You can lose part or all of the capital
          you trade. Leveraged products (crypto perpetuals, futures, options) can produce losses larger than
          the margin you deposit.
        </P>
        <P>
          <strong>Never trade money you can't afford to lose.</strong> Never disable the daily-loss circuit
          breaker because "I'll make it back". Past returns of any strategy, indicator, or signal source —
          including the in-house Sniper / RockBand / DailyBlast scanners — are <em>not</em> indicative of
          future performance.
        </P>
      </GuideCallout>

      <H id="no-advice">Not investment advice</H>
      <P>
        Nothing in this app or its documentation constitutes investment advice. The signals emitted by
        in-house strategies, the alerts pushed from your TradingView Pine scripts, and the analytical
        overlays on charts are <strong>tools</strong>, not recommendations. Every trade you place is your
        decision; you bear all losses and keep all gains.
      </P>

      <H id="paper-first">Validate before going live</H>
      <P>
        Before turning Auto Trade ON, validate a strategy with <strong>Auto Trade OFF (alert-only)</strong>{' '}
        for at least two weeks. Alerts arrive in the Alerts list but no order is placed; you click Execute
        only when you'd genuinely want the trade. Watch what your hand-picks would have done versus the full
        alert stream.
      </P>
      <GuideCallout kind="warning">
        There is no paper / simulator mode — <strong>all trades go to the exchange</strong>, whether placed
        manually or by Auto Trade. The single cut-off is the <strong>Order Placement</strong> kill switch on
        the Dashboard, which blocks new entry orders (exits always go through). Treat Auto Trade as live
        money from the first signal.
      </GuideCallout>
      <P>
        Only flip Auto Trade ON once you've personally observed the strategy across several market regimes —
        opening drives, consolidation, news days, expiry days — and you can reach the Order Placement switch
        to cut off entries instantly.
      </P>

      <H id="broker-relationship">Broker relationship</H>
      <P>
        Your account at Delta Exchange / Zerodha is yours. earngush is a client that connects via API keys
        you generate. Funds are held at the broker; earngush has no custodial relationship with your money.
        Withdrawals always require a broker-side action — your API key permissions in earngush should NEVER
        include Withdrawal scope.
      </P>

      <H id="data-handling">Your data — what we store</H>
      <P>
        Stored on the earngush server:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Email + name + bcrypt-hashed password." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>Broker API keys + secrets — <strong>AES-256-GCM encrypted at rest</strong>.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Your alerts, trades, settings, watchlists, instrument prefs, HVN levels." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Server-side logs of every webhook, broker call, gate decision, scheduled-task run." />
        </ListItem>
      </List>
      <P>
        Not stored:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Your broker username / login password (we use API keys, not session credentials)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Your bank details, KYC docs, PAN — none of those touch earngush." />
        </ListItem>
      </List>

      <H id="regulatory">Regulatory framing (India)</H>
      <P>
        earngush is a technology tool that automates execution of <em>your</em> trading decisions or
        signals from sources <em>you choose</em>. It is not a SEBI-registered Investment Adviser or
        Research Analyst. The in-house Sniper / RockBand / DailyBlast scanners are algorithmic signal
        generators — using them is your discretionary choice.
      </P>
      <P>
        If you redistribute earngush's in-house signals to other people for a fee (Telegram tip group,
        paid newsletter), <em>that</em> distribution likely requires SEBI registration on your end —
        consult a lawyer.
      </P>

      <H id="terms-privacy">Terms of Service + Privacy Policy</H>
      <P>
        Full T&C and Privacy Policy documents are <strong>in preparation</strong> with legal counsel and
        will be linked here before unrestricted public release. The summary above (what we store / what we
        don't / no investment advice / broker custody) is non-binding plain-English context, not the
        binding legal terms.
      </P>
      <GuideCallout kind="warning">
        If you're using earngush before the formal T&C ships, you're doing so as an early-access friends-
        and-family user. By placing trades you acknowledge the Market Risk Warning above and accept that
        you bear all trading losses.
      </GuideCallout>

      <H id="account-deletion">Account deletion</H>
      <P>
        Currently admin-driven — contact the administrator. A self-service "delete my account" flow is
        planned per the PRE_LAUNCH_REVIEW CL4 item, alongside a "data export" flow (GDPR Article 15).
      </P>

      <H id="contact">Contact</H>
      <P>
        For security issues, T&C questions, or anything legal: email the admin (shreya.g.nandy@gmail.com)
        directly. Do not file security issues in public GitHub trackers.
      </P>
    </>
  ),
  searchIndex: [
    { heading: 'Market Risk Warning',         anchor: 'risk-warning',     body: 'risk warning trading loss capital leverage' },
    { heading: 'Not investment advice',       anchor: 'no-advice',        body: 'not investment advice tools recommendations decision' },
    { heading: 'Validate before going live',  anchor: 'paper-first',      body: 'validate auto trade off alert only two weeks no paper simulator kill switch order placement' },
    { heading: 'Broker relationship',         anchor: 'broker-relationship', body: 'broker relationship api keys withdrawal custody' },
    { heading: 'Your data',                   anchor: 'data-handling',    body: 'data handling stored encryption bcrypt aes' },
    { heading: 'Regulatory framing (India)',  anchor: 'regulatory',       body: 'regulatory sebi investment adviser research analyst' },
    { heading: 'T&C + Privacy',               anchor: 'terms-privacy',    body: 'terms of service privacy policy lawyer' },
    { heading: 'Account deletion',            anchor: 'account-deletion', body: 'account deletion data export gdpr' },
    { heading: 'Contact',                     anchor: 'contact',          body: 'contact email security legal' },
  ],
};

export default Section_legal;
